/**
 * response.ts — the calculation response envelope (FR-22, FR-23, FR-24, ARCHITECTURE.md §4.5).
 *
 * The envelope is `{ engineVersion, catalogVersion, checks[], values, max, els }`. It is what every
 * calculation endpoint returns, what a saved case stores as its frozen result snapshot (D10), and
 * what P2's recalculation diff aligns on. It is therefore a shape we are stuck with: the only
 * cheap moment to get it right is now.
 *
 * RULES THIS FILE OBEYS
 *  - No formulas. Nothing here computes an engineering value. The one piece of logic present is
 *    `checkStatusFromRatio`, which is a threshold *classification* copied from the oracle's UI —
 *    it multiplies and divides nothing (see its citation comment).
 *  - No import of `@ronceray/engine`. The contract is imported by the browser; the engine is
 *    server-only (FR-3). This is why `checks[].id` is typed as a plain `string` here rather than
 *    as a union of the 37 registered ids: the register lives in `packages/engine/check-register.ts`
 *    because the engine has zero dependencies (FR-2) and so cannot import the contract, and the
 *    contract must not import the engine. The register is *documented* in `CHECKS.md`, and the
 *    consistency test that ties the two together is US-017's job.
 *  - Every field carries a `.describe()` with its unit where a unit applies (FR-28). Units follow
 *    `UNITS.md`: kN, kN·m, mm, kN/m as the baseline, with the documented deviations.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * NON-FINITE NUMBERS — a deliberate decision, not an oversight
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * The frozen engine genuinely emits `Infinity` and `NaN` for degenerate inputs (the canonical case:
 * `bois` with `lappui: 0` makes the transverse-compression check's `taux` equal `Infinity`). The
 * engine is frozen bug-for-bug under FR-18, so those values are correct output, not corruption.
 *
 * Zod 4's `z.number()` REJECTS `Infinity`, `-Infinity` and `NaN`. Using it for engine-derived
 * numbers would make the schema reject real engine output — a validation layer that calls the
 * source of truth invalid.
 *
 * Compounding this, `JSON.stringify` serialises all three as `null`. So the *same* engine value has
 * two legal forms depending on whether it has crossed the wire yet:
 *
 *     in-process : { taux: Infinity }        after JSON round-trip : { taux: null }
 *
 * DECISION: every number that originates in the engine is modelled by `engineNumber`, which accepts
 * any JavaScript number including the non-finite ones; and wherever the JSON round-trip can turn
 * one into `null`, the field is nullable. Consumers must treat "non-finite" and "null" as the same
 * thing — *not a usable number* — because they are the same thing seen at two points in the
 * pipeline. For `checks[].ratio` that collapse is free: the oracle already emits `taux: null` for a
 * not-applicable check, and `checkStatusFromRatio` maps null and non-finite alike to `'na'`, so no
 * meaning is lost across the boundary.
 *
 * The one exception is `max`, which is not nullable — see its comment.
 */

import { z } from 'zod';

/* ================================================================================================
 * Primitives
 * ==============================================================================================*/

/**
 * A number as the engine produces it: finite, `Infinity`, `-Infinity` or `NaN`.
 *
 * `z.number()` is deliberately NOT used — in Zod 4 it rejects all three non-finite forms, which
 * would make this schema reject genuine output from the frozen engine. See the non-finite decision
 * in this file's header.
 */
/*
 * Exported since P2/US-208: the recalculation diff describes engine ratios, and a diff schema that
 * used `z.number()` would reject the very non-finite values the header above explains the engine
 * genuinely produces. One definition, imported — not a second, looser copy in `./cases`.
 */
export const engineNumber = z.custom<number>((value) => typeof value === 'number', {
  message: 'Expected a number (Infinity, -Infinity and NaN are accepted — see CHECKS.md)',
});

/**
 * An engine number that may also arrive as `null`, which is what `JSON.stringify` produces for a
 * non-finite value. Semantically identical to a non-finite number: no usable magnitude.
 */
export const engineNumberOrNull = engineNumber.nullable();

/* ================================================================================================
 * Intermediate values — the `values` bag (D9, FR-24)
 * ==============================================================================================*/

/**
 * One entry of the intermediate-values bag.
 *
 * Not numeric-only, and this is measured rather than assumed: `lamine` emits `v.voilement` and
 * `v.fam` as strings, `creux` emits `v.courbe` as a string. `boolean` is accepted because the bag
 * is passed through verbatim and nothing forbids the engine adding one in a minor bump. `null`
 * covers the JSON round-trip of a non-finite intermediate.
 */
export const IntermediateValueSchema = z
  .union([engineNumber, z.string(), z.boolean(), z.null()])
  .describe(
    'One intermediate value, passed through from the engine verbatim. Units are per-key and are ' +
      'documented in UNITS.md — the bag mixes kN, kN·m, mm, MPa, mm², mm³, mm⁴, kg/m, degrees and ' +
      'dimensionless factors, so no single unit applies to the container. A string value is a ' +
      'classification, not a measurement (e.g. buckling curve, section family, web-buckling ' +
      'verdict). null is the JSON form of a non-finite value.',
  );

/**
 * The full intermediate-values bag: the oracle's `v` object, verbatim.
 *
 * Deliberately permissive about which keys are present. The bag is module-specific and large — 79
 * keys for `lamine`, 81 for `creux`, 46 for `bois`, with no common subset worth enumerating — and
 * FR-24 forbids trimming it. Pinning the key set here would mean re-freezing this file every time
 * the engine exposes one more intermediate, for no consumer benefit: the note de calcul and the
 * P2 diff read the bag by name, not by exhaustive destructuring.
 */
export const IntermediateValuesSchema = z
  .record(z.string(), IntermediateValueSchema)
  .describe(
    "The engine's intermediate-values bag (the oracle's `v` object) passed through verbatim, " +
      'keyed by the original engine name. Module-specific: ~79 keys for lamine, ~81 for creux, ' +
      '~46 for bois. Per-key units are in UNITS.md. Mixed units — see IntermediateValueSchema.',
  );

export type IntermediateValue = z.infer<typeof IntermediateValueSchema>;
export type IntermediateValues = z.infer<typeof IntermediateValuesSchema>;

/* ================================================================================================
 * Check status
 * ==============================================================================================*/

/**
 * Verification outcome for a single check.
 *
 *  - `ok`   — utilisation within the acceptable band
 *  - `warn` — utilisation high but still within 1.0
 *  - `ko`   — utilisation above 1.0, the check fails
 *  - `na`   — the check does not apply to this load case, or produced no usable number
 *
 * A `ko` is an engineering result, not an HTTP error: a failed check returns 200 (FR-25).
 */
export const CheckStatusSchema = z
  .enum(['ok', 'warn', 'ko', 'na'])
  .describe(
    'Verification outcome. Dimensionless. ok = within limits, warn = high utilisation but ' +
      'below 1.0, ko = utilisation above 1.0 (the check fails), na = not applicable to this load ' +
      'case or no usable ratio. A ko is a valid 200 response, never an error.',
  );

export type CheckStatus = z.infer<typeof CheckStatusSchema>;

/**
 * Utilisation strictly above this value is `ko`. Dimensionless.
 *
 * SOURCE — the oracle's own UI classifier, quoted verbatim from
 * `tools/golden/oracle/Calcul_structures_EC3_EC5.frozen.html`:
 *
 *     function niveau(t) { return t > 1 ? 'ko' : t > 0.85 ? 'warn' : 'ok'; }
 *
 * These two constants and `checkStatusFromRatio` below are that line and nothing else. They are
 * the single source of truth for the thresholds in this repository; the FR-9 adapter classifies
 * with the identical values (it cannot import them — the engine has zero dependencies — so US-017
 * asserts the two agree). Changing either number changes what a control office is told about a
 * member, so it is an engineering decision, not a styling one.
 */
export const CHECK_STATUS_KO_ABOVE = 1;

/** Utilisation strictly above this value (and not above `CHECK_STATUS_KO_ABOVE`) is `warn`. */
export const CHECK_STATUS_WARN_ABOVE = 0.85;

/**
 * Classify a utilisation ratio. Threshold comparison only — no arithmetic on engineering values.
 *
 * Extends the oracle's `niveau()` with `na`, which the oracle's UI expressed by simply skipping the
 * row. `null` (not applicable, or the JSON form of a non-finite value) and a live non-finite number
 * are the same case and both classify as `na`; see this file's header for why they are equivalent.
 */
export function checkStatusFromRatio(ratio: number | null | undefined): CheckStatus {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) return 'na';
  if (ratio > CHECK_STATUS_KO_ABOVE) return 'ko';
  if (ratio > CHECK_STATUS_WARN_ABOVE) return 'warn';
  return 'ok';
}

/* ================================================================================================
 * One check
 * ==============================================================================================*/

/**
 * One verification line.
 *
 * D9 — INTERMEDIATE VALUES, a deliberate v1 decision (FR-24):
 * Full intermediate values are carried by the envelope's TOP-LEVEL `values` field, which holds the
 * oracle's `v` object verbatim. The per-check `values` field below is OPTIONAL in schema v1 and the
 * current engine never populates it. That is not laziness: the engine does not attribute its
 * intermediates to individual checks — `v` is one flat bag shared by every check in the module —
 * and inventing that redistribution in P0 would mean rewriting the calculation path, which is
 * exactly what P0 exists to avoid (FR-5 / D2). The field is present in the schema so that a later
 * engine can populate it without a breaking envelope change; a consumer must never assume it is
 * there, and must read the top-level `values` for the note de calcul.
 */
export const CheckResultSchema = z
  .object({
    id: z
      .string()
      .min(1)
      .describe(
        'Permanent stable identifier for this verification, e.g. "lamine.flexion_y". ' +
          'Dimensionless. Assigned from the check register (packages/engine/src/check-register.ts, ' +
          'documented in CHECKS.md). Ids are permanent, never reused and never positional — they ' +
          "are the alignment key for P2's recalculation diff (D10).",
      ),
    label: z
      .string()
      .describe(
        'The French label the engine emits for this check, passed through verbatim. ' +
          'Dimensionless. NOT a unique key: bois emits two pairs of duplicate labels — see ' +
          'CHECKS.md. Use `id`.',
      ),
    eq: z
      .string()
      .describe(
        'Eurocode equation reference the engine emits, parentheses included, e.g. "(6.12)". ' +
          'Dimensionless. Passed through verbatim.',
      ),
    ratio: engineNumberOrNull.describe(
      'Utilisation ratio (design effect over design resistance). Dimensionless. null when the ' +
        'check does not apply to this load case — the engine emits `taux: null` there — and also ' +
        'the JSON form of a non-finite ratio. May be non-finite in degenerate cases before ' +
        'serialisation. Either way the status is "na".',
    ),
    status: CheckStatusSchema,
    values: IntermediateValuesSchema.optional().describe(
      'OPTIONAL per-check intermediates. Dimensionless container; per-key units in UNITS.md. ' +
        "Not populated in schema v1 — read the envelope's top-level `values` instead. See the " +
        'D9 decision above this schema and in CHECKS.md.',
    ),
  })
  .describe('One verification line: its stable id, the engine label and equation, and its result.');

export type CheckResult = z.infer<typeof CheckResultSchema>;

/* ================================================================================================
 * Serviceability (ELS) block — NOT one shape
 * ==============================================================================================*/

/**
 * THE ELS TRAP, stated once so no consumer has to rediscover it:
 *
 * `els` is two different shapes, measured against the frozen oracle.
 *   - `lamine` and `creux` emit 17 keys (the shared `fleches` helper's output).
 *   - `bois` emits 6 keys (model-supplied instantaneous deflections, creep-adjusted).
 *   - Only `wlim`, `maxELS` and `ok` are common to all three.
 *
 * `taux` exists ONLY on timber; `tauxTot` / `tauxQ` exist ONLY on steel. A single flat interface
 * covering both would claim that a steel beam has a `winst`, which is false — so this is modelled
 * as a union of the two real shapes. Discriminate structurally (a `taux` key means timber), or
 * carry the module alongside.
 *
 * Both variants are LOOSE objects: unknown keys are preserved rather than stripped. FR-24 forbids
 * trimming engine output, and a minor engine bump that adds a deflection field must not be silently
 * deleted by the validation layer on its way to the note de calcul.
 */
export const SteelDeflectionBlockSchema = z
  .looseObject({
    Cq: engineNumberOrNull.describe(
      'Deflection coefficient for the distributed load under the selected static scheme. ' +
        'Dimensionless.',
    ),
    Cp: engineNumberOrNull.describe(
      'Deflection coefficient for the point load under the selected static scheme. Dimensionless.',
    ),
    wGy: engineNumberOrNull.describe('Deflection under permanent load G, strong axis, mm.'),
    wQy: engineNumberOrNull.describe('Deflection under variable load Q, strong axis, mm.'),
    wGz: engineNumberOrNull.describe('Deflection under permanent load G, weak axis, mm.'),
    wQz: engineNumberOrNull.describe('Deflection under variable load Q, weak axis, mm.'),
    wG: engineNumberOrNull.describe(
      'Deflection under G actually used: the computed value, or the manually entered one when ' +
        'the deflection mode is manual, mm.',
    ),
    wQ: engineNumberOrNull.describe(
      'Deflection under Q actually used: the computed value, or the manually entered one when ' +
        'the deflection mode is manual, mm.',
    ),
    wtoty: engineNumberOrNull.describe('Total deflection under G + Q, strong axis, mm.'),
    wtotz: engineNumberOrNull.describe('Total deflection under G + Q, weak axis, mm.'),
    wtot: engineNumberOrNull.describe('Governing total deflection under G + Q, mm.'),
    wlim: engineNumberOrNull.describe(
      'Limit for the total deflection, L/n1 with n1 the total-deflection criterion, mm. ' +
        '(n1 itself is dimensionless — it is a divisor, not a length.)',
    ),
    wlimQ: engineNumberOrNull.describe(
      'Limit for the variable-load deflection, L/n2 with n2 the variable-deflection criterion, mm.',
    ),
    tauxTot: engineNumberOrNull.describe(
      'Utilisation of the total deflection against its limit. Dimensionless.',
    ),
    tauxQ: engineNumberOrNull.describe(
      'Utilisation of the variable-load deflection against its limit. Dimensionless.',
    ),
    maxELS: engineNumberOrNull.describe(
      'Governing serviceability utilisation across the deflection criteria. Dimensionless. NOT ' +
        "folded into the envelope's `max`, which is ELU only — see `max`.",
    ),
    ok: z
      .boolean()
      .describe('True when every serviceability criterion is satisfied. Dimensionless.'),
  })
  .describe(
    'Serviceability block for the steel modules (lamine, creux): 17 keys from the shared ' +
      'deflection helper. All deflections in mm, all utilisations dimensionless.',
  );

export const TimberDeflectionBlockSchema = z
  .looseObject({
    winst: engineNumberOrNull.describe(
      'Instantaneous deflection under the characteristic combination, mm. Built from the ' +
        'model-supplied instantaneous deflections under G and Q — the timber module does not ' +
        'compute deflections from loads.',
    ),
    wfin: engineNumberOrNull.describe(
      'Final deflection including creep (kdef), quasi-permanent combination, mm.',
    ),
    wlim: engineNumberOrNull.describe(
      'Deflection limit, L/nlim with nlim the deflection criterion, mm. (nlim itself is ' +
        'dimensionless — it is a divisor, not a length.)',
    ),
    taux: engineNumberOrNull.describe(
      'Utilisation of the final deflection against its limit. Dimensionless. Exists only on ' +
        'timber; the steel block carries tauxTot / tauxQ instead.',
    ),
    maxELS: engineNumberOrNull.describe(
      "Governing serviceability utilisation. Dimensionless. NOT folded into the envelope's " +
        '`max`, which is ELU only — see `max`.',
    ),
    ok: z.boolean().describe('True when the serviceability criterion is satisfied. Dimensionless.'),
  })
  .describe(
    'Serviceability block for the timber module (bois): 6 keys, from model-supplied ' +
      'instantaneous deflections adjusted for creep. Deflections in mm, utilisations dimensionless.',
  );

/**
 * The serviceability block, either shape. The union is unambiguous by construction: the steel
 * variant requires 14 keys the timber block does not have, and the timber variant requires 3 keys
 * the steel block does not have, so exactly one branch can ever match.
 */
export const DeflectionBlockSchema = z
  .union([SteelDeflectionBlockSchema, TimberDeflectionBlockSchema])
  .describe(
    'Serviceability (ELS) results. Two distinct shapes: 17 keys for steel (lamine, creux), 6 for ' +
      'timber (bois). Only wlim (mm), maxELS (dimensionless) and ok are common to both.',
  );

export type SteelDeflectionBlock = z.infer<typeof SteelDeflectionBlockSchema>;
export type TimberDeflectionBlock = z.infer<typeof TimberDeflectionBlockSchema>;
export type DeflectionBlock = z.infer<typeof DeflectionBlockSchema>;

/* ================================================================================================
 * The envelope
 * ==============================================================================================*/

/**
 * The response envelope returned by every calculation endpoint (FR-22, ARCHITECTURE.md §4.5).
 *
 * A LOOSE object on purpose. The six fields below are required and are the contract; unknown extra
 * keys are preserved rather than stripped, because the raw engine result also carries `ok` and
 * `poids` and FR-24's no-trimming rule means a validation pass must not be the thing that deletes
 * them. Promoting either to a first-class envelope field is a contract change, not something to do
 * by accident.
 */
export const CalcResponseSchema = z
  .looseObject({
    engineVersion: z
      .string()
      .min(1)
      .describe(
        'Semver version of the calculation engine that produced this result, e.g. "1.0.0". ' +
          'Dimensionless. Engineering-driven semantics (FR-6): major = any changed output for any ' +
          'existing input. Stored with a saved case (D7) so a recalculation can say what changed.',
      ),
    catalogVersion: z
      .string()
      .min(1)
      .describe(
        'Version of the section / material catalog used, e.g. "2026.08". Dimensionless. Bumped on ' +
          'any data change — a corrected section modulus changes results exactly like a formula ' +
          'change — and stored with a saved case (D7).',
      ),
    checks: z
      .array(CheckResultSchema)
      .describe(
        'Every ultimate-limit-state verification the module performed, in engine emission order. ' +
          '12 entries for lamine, 15 for creux, 10 for bois — see CHECKS.md. Order is descriptive, ' +
          'never the identity: align on `id`.',
      ),
    values: IntermediateValuesSchema.describe(
      "The engine's full intermediate-values bag, verbatim and untrimmed (D9 / FR-24). This is " +
        'where every intermediate lives in schema v1 — the per-check `values` is optional and ' +
        'unpopulated. Mixed units, documented per key in UNITS.md.',
    ),
    max: engineNumber.describe(
      'Governing ULTIMATE-limit-state utilisation: the largest usable ratio across `checks`, or 0 ' +
        'when no check produced one. Dimensionless. ELU ONLY — serviceability is NOT folded in. ' +
        'A UI banner that wants the overall verdict must combine this with `els.maxELS` itself, ' +
        'exactly as the oracle does; redefining `max` to include ELS would silently change every ' +
        'stored result. Finite by construction (the engine skips null and non-finite ratios when ' +
        'taking the maximum), which is why this one field is not nullable.',
    ),
    els: DeflectionBlockSchema,
  })
  .describe(
    'Calculation response envelope. Returned with HTTP 200 even when a check fails — a failing ' +
      'check is an engineering result, not an error (FR-25).',
  );

export type CalcResponse = z.infer<typeof CalcResponseSchema>;
